import React, { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check if user is logged in on app start
    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const adminUser = localStorage.getItem('adminUser');

            if (token && adminUser) {
                // Verify token with server
                const profile = await authAPI.getProfile();
                if (profile && profile.admin) {
                    setAdmin(profile.admin);
                    setIsAuthenticated(true);
                } else if (profile && profile.id) {
                    // Handle case where admin data is returned directly
                    setAdmin(profile);
                    setIsAuthenticated(true);
                } else {
                    throw new Error('Invalid profile data');
                }
            }
        } catch (error) {
            // Token is invalid, clear storage
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (credentials) => {
        try {
            if (!credentials || !credentials.email || !credentials.password) {
                throw new Error('Invalid credentials provided');
            }

            const response = await authAPI.login(credentials);

            if (!response || !response.token) {
                throw new Error('Invalid response from server');
            }

            // Create admin object from response (backend returns admin data directly)
            const adminData = {
                id: response.id,
                username: response.username,
                email: response.email,
                role: response.role
            };

            // Store token and user data
            localStorage.setItem('adminToken', response.token);
            localStorage.setItem('adminUser', JSON.stringify(adminData));

            setAdmin(adminData);
            setIsAuthenticated(true);

            return response;
        } catch (error) {
            throw error;
        }
    };

    const logout = () => {
        try {
            // Clear storage
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');

            // Clear state
            setAdmin(null);
            setIsAuthenticated(false);
        } catch (error) {
            // no-op
        }
    };

    const changePassword = async (passwordData) => {
        try {
            if (!passwordData || !passwordData.currentPassword || !passwordData.newPassword) {
                throw new Error('Invalid password data provided');
            }

            const response = await authAPI.changePassword(passwordData);
            return response;
        } catch (error) {
            throw error;
        }
    };

    const value = {
        admin,
        loading,
        isAuthenticated,
        login,
        logout,
        changePassword
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 